import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "cleaning-web-design-colorado",
  metaTitle: "Cleaning Service Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom cleaning web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Cleaning marketing", to: "/cleaning-marketing-colorado" },
  ],
  currentLabel: "Cleaning Service Web Design Colorado",
  eyebrow: "Cleaning Service Web Design — Colorado",
  h1: (
    <>
      Cleaning Service <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded cleaning websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Cleaning websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first cleaning sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Cleaning Service web design in Colorado",
    body: "Cleaning Service web design is the practice of building conversion-focused, mobile-first websites for cleaning companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with instant quote calculators above the fold." },
    { title: "Instant pricing calculators", body: "Square footage + bedroom/bathroom calculators that quote and book in under 60 seconds." },
    { title: "Service pages per type", body: "Recurring, deep, move-in/out, post-construction, Airbnb turnover, commercial — each ranks independently." },
    { title: "Online booking integration", body: "Direct integration with Launch27, BookingKoala, or ZenMaid for real-time booking." },
    { title: "Trust signals above the fold", body: "Background-check badges, bonding, and insurance proof where prospects look first." },
    { title: "Schema and SEO baked in", body: "LocalBusiness, Service, FAQPage, Review schema at launch." },
  ],
  whyThisMarket: [
    { heading: "Instant pricing wins the booking", body: "Cleaning is a commodity buy until you remove price friction. Sites with instant quote calculators book 3x more." },
    { heading: "Recurring vs one-time needs different pages", body: "Recurring residential, deep clean, and Airbnb turnover are different searches and different conversion flows." },
    { heading: "Trust signals decide who they let in", body: "Background checks, bonding, and reviews surfaced above the fold — they're letting strangers into their home." },
  ],
  relatedLinks: [
    { to: "/cleaning-marketing-colorado", label: "Cleaning marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado cleaning case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a cleaning website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a cleaning website?", a: "Typical timeline is 4–6 weeks for a 12–20 page cleaning site with full service and service-area pages. Larger multi-location cleaning companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs cleaning companies?", a: "No — we build cleaning sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Cleaning Service Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for cleaning companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/cleaning-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
